import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  TextInput,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { RootStackParamList, OptionListType, CustomOptionValue } from '../types';
import { optionListsService } from '../services/optionLists';
import { adminService } from '../services/admin';
import { useDialog } from '../contexts';
import { Colors } from '../constants';
import { Button } from '../components';

type Props = NativeStackScreenProps<RootStackParamList, 'AdminCustomValues'>;

const LIST_TYPES: { key: OptionListType; labelKey: string }[] = [
  { key: 'category', labelKey: 'screens.adminOptionLists.tabs.category' },
  { key: 'condition', labelKey: 'screens.adminOptionLists.tabs.condition' },
  { key: 'size', labelKey: 'screens.adminOptionLists.tabs.size' },
  { key: 'sport_type', labelKey: 'screens.adminOptionLists.tabs.sportType' },
];

export const AdminCustomValuesScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const [activeTab, setActiveTab] = useState<OptionListType>('category');
  const [customValues, setCustomValues] = useState<CustomOptionValue[]>([]);
  const [loading, setLoading] = useState(true);

  // Promotion form state
  const [promoting, setPromoting] = useState<CustomOptionValue | null>(null);
  const [selectedSimilar, setSelectedSimilar] = useState<string[]>([]);
  const [similarValues, setSimilarValues] = useState<CustomOptionValue[]>([]);
  const [formLabelEn, setFormLabelEn] = useState('');
  const [formLabelNl, setFormLabelNl] = useState('');

  // Route guard
  useEffect(() => {
    const checkAdminStatus = async () => {
      const isAdmin = await adminService.isCurrentUserAdmin();
      if (!isAdmin) {
        await dialog.alert(
          t('common.errors.unauthorized'),
          t('screens.adminDashboard.notAuthorized'),
          'error'
        );
        navigation.goBack();
      }
    };
    checkAdminStatus();
  }, []);

  // Load custom values for active tab
  const loadCustomValues = useCallback(async () => {
    setLoading(true);
    try {
      const data = await optionListsService.getPendingCustomValues(activeTab);
      setCustomValues(data);
    } catch (error) {
      console.error('Failed to load custom values:', error);
      dialog.alert(t('common.errors.generic'), String(error), 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadCustomValues();
  }, [loadCustomValues]);

  const startPromotion = async (customValue: CustomOptionValue) => {
    setPromoting(customValue);
    setFormLabelEn(customValue.value);
    setFormLabelNl('');
    setSelectedSimilar([customValue.id]);

    // Find similar values
    try {
      const similar = await optionListsService.findSimilarCustomValues(
        activeTab,
        customValue.normalized_value
      );
      setSimilarValues(similar);
    } catch (error) {
      console.error('Failed to find similar values:', error);
      setSimilarValues([]);
    }
  };

  const cancelPromotion = () => {
    setPromoting(null);
    setSelectedSimilar([]);
    setSimilarValues([]);
    setFormLabelEn('');
    setFormLabelNl('');
  };

  const toggleSimilarSelection = (id: string) => {
    setSelectedSimilar((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handlePromote = async () => {
    if (!formLabelEn.trim() || !formLabelNl.trim()) {
      dialog.alert(
        t('common.validation.required'),
        t('screens.adminCustomValues.form.translationsRequired'),
        'warning'
      );
      return;
    }

    const confirmed = await dialog.confirm(
      t('screens.adminCustomValues.promote.confirmTitle'),
      t('screens.adminCustomValues.promote.confirmMessage', {
        count: selectedSimilar.length,
      }),
      { variant: 'primary' }
    );

    if (!confirmed) return;

    try {
      const result = await optionListsService.promoteCustomValues(selectedSimilar, {
        list_type: activeTab,
        value: promoting!.normalized_value,
        label_en: formLabelEn.trim(),
        label_nl: formLabelNl.trim(),
        sort_order: 50, // Default sort order
        is_active: true,
        scope_type: null,
        scope_value: null,
      });

      cancelPromotion();
      await loadCustomValues();

      dialog.alert(
        t('common.success'),
        t('screens.adminCustomValues.promote.success', {
          count: result.updatedItemsCount,
        }),
        'success'
      );
    } catch (error) {
      console.error('Failed to promote:', error);
      dialog.alert(t('common.errors.generic'), String(error), 'error');
    }
  };

  const getTotalUsageCount = () => {
    const promotingValue = customValues.find((cv) => cv.id === promoting?.id);
    const similarSelected = similarValues.filter((sv) =>
      selectedSimilar.includes(sv.id)
    );
    const total =
      (promotingValue?.usage_count || 0) +
      similarSelected.reduce((sum, sv) => sum + sv.usage_count, 0);
    return total;
  };

  return (
    <View style={styles.wrapper}>
      {/* Header */}
      <View style={[styles.headerContainer, Platform.OS === 'web' && styles.headerContainerWeb]}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('AdminDashboard')}
          >
            <Ionicons name="arrow-back" size={24} color="#007AFF" />
            <Text style={styles.backText}>{t('common.buttons.back')}</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('screens.adminCustomValues.title')}</Text>
        </View>
      </View>

      {/* Tabs */}
      <View style={styles.tabsContainer}>
        {LIST_TYPES.map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[styles.tab, activeTab === tab.key && styles.tabActive]}
            onPress={() => {
              setActiveTab(tab.key);
              cancelPromotion();
            }}
          >
            <Text style={[styles.tabText, activeTab === tab.key && styles.tabTextActive]}>
              {t(tab.labelKey)}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
        {loading ? (
          <Text style={styles.loadingText}>{t('common.loading')}</Text>
        ) : customValues.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>{t('screens.adminCustomValues.noCustomValues')}</Text>
          </View>
        ) : (
          <View style={styles.valuesList}>
            {customValues.map((cv) => (
              <View key={cv.id} style={styles.valueRow}>
                <View style={styles.valueInfo}>
                  <Text style={styles.valueText}>{cv.value}</Text>
                  <Text style={styles.valueMeta}>
                    {t('screens.adminCustomValues.usedTimes', { count: cv.usage_count })}
                  </Text>
                  <Text style={styles.valueDate}>
                    {t('screens.adminCustomValues.firstUsed')}: {new Date(cv.first_used_at).toLocaleDateString()}
                  </Text>
                </View>
                <TouchableOpacity
                  style={styles.promoteButton}
                  onPress={() => startPromotion(cv)}
                >
                  <Text style={styles.promoteButtonText}>
                    {t('screens.adminCustomValues.promote.button')}
                  </Text>
                </TouchableOpacity>
              </View>
            ))}
          </View>
        )}

        {/* Promotion Form */}
        {promoting && (
          <View style={styles.promotionForm}>
            <Text style={styles.formTitle}>
              {t('screens.adminCustomValues.promote.title', { value: promoting.value })}
            </Text>

            {/* Similar values */}
            {similarValues.length > 0 && (
              <View style={styles.similarSection}>
                <Text style={styles.similarTitle}>
                  {t('screens.adminCustomValues.promote.similarFound')}
                </Text>
                {similarValues.map((sv) => (
                  <TouchableOpacity
                    key={sv.id}
                    style={styles.similarRow}
                    onPress={() => toggleSimilarSelection(sv.id)}
                  >
                    <View
                      style={[
                        styles.checkbox,
                        selectedSimilar.includes(sv.id) && styles.checkboxChecked,
                      ]}
                    >
                      {selectedSimilar.includes(sv.id) && (
                        <Ionicons name="checkmark" size={14} color={Colors.white} />
                      )}
                    </View>
                    <Text style={styles.similarText}>
                      "{sv.value}" ({sv.usage_count}x)
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            <Text style={styles.totalCount}>
              {t('screens.adminCustomValues.promote.totalUsage', {
                count: getTotalUsageCount(),
              })}
            </Text>

            <Text style={styles.formLabel}>{t('screens.adminOptionLists.form.labelEn')}</Text>
            <TextInput
              style={styles.formInput}
              value={formLabelEn}
              onChangeText={setFormLabelEn}
              placeholder="English label"
            />

            <Text style={styles.formLabel}>{t('screens.adminOptionLists.form.labelNl')}</Text>
            <TextInput
              style={styles.formInput}
              value={formLabelNl}
              onChangeText={setFormLabelNl}
              placeholder="Dutch label"
            />

            <View style={styles.formButtons}>
              <Button
                title={t('screens.adminCustomValues.promote.confirmButton')}
                onPress={handlePromote}
              />
              <Button
                title={t('common.buttons.cancel')}
                onPress={cancelPromotion}
                variant="outline"
                style={styles.cancelButton}
              />
            </View>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  headerContainer: {
    backgroundColor: Colors.white,
    paddingTop: Platform.OS === 'ios' ? 50 : 20,
    paddingBottom: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  headerContainerWeb: {
    paddingTop: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
  },
  backText: {
    color: '#007AFF',
    fontSize: 16,
    marginLeft: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: Colors.text,
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabActive: {
    borderBottomColor: Colors.primary,
  },
  tabText: {
    fontSize: 14,
    color: Colors.textMedium,
  },
  tabTextActive: {
    color: Colors.primary,
    fontWeight: '600',
  },
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  loadingText: {
    textAlign: 'center',
    color: Colors.textMedium,
    padding: 20,
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    color: Colors.textMedium,
    fontSize: 16,
  },
  valuesList: {
    backgroundColor: Colors.white,
    borderRadius: 8,
    overflow: 'hidden',
  },
  valueRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderLight,
  },
  valueInfo: {
    flex: 1,
  },
  valueText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text,
  },
  valueMeta: {
    fontSize: 14,
    color: Colors.primary,
    fontWeight: '500',
    marginTop: 2,
  },
  valueDate: {
    fontSize: 12,
    color: Colors.textLight,
    marginTop: 2,
  },
  promoteButton: {
    backgroundColor: Colors.primary,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  promoteButtonText: {
    color: Colors.white,
    fontSize: 14,
    fontWeight: '500',
  },
  promotionForm: {
    backgroundColor: Colors.white,
    borderRadius: 8,
    padding: 16,
    marginTop: 16,
  },
  formTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text,
    marginBottom: 16,
  },
  similarSection: {
    backgroundColor: Colors.backgroundLight,
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  similarTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.textDark,
    marginBottom: 8,
  },
  similarRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: Colors.border,
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: Colors.primary,
    borderColor: Colors.primary,
  },
  similarText: {
    fontSize: 14,
    color: Colors.text,
  },
  totalCount: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.primary,
    marginBottom: 16,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.textDark,
    marginBottom: 4,
    marginTop: 12,
  },
  formInput: {
    borderWidth: 1,
    borderColor: Colors.border,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: Colors.text,
    backgroundColor: Colors.white,
  },
  formButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 20,
  },
  cancelButton: {
    flex: 0,
  },
});
